import * as $ from 'jquery';
import { disposableSubscription } from 'common/barrels/core';
import { BehaviorSubject, Observable } from 'common/barrels/rx';
import { escapeRegex } from 'common/functions/string';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { registerBinding } from 'runtime/bindingengine';
import * as rxui from 'common/rxui/core';
import { default as Decimal } from 'decimal.js';

import 'runtime/styles/controls/numericbox.less';

var maxValue = new Decimal('79228162514264337593543950335');
var minValue = new Decimal('-79228162514264337593543950335');

export class NumericBox extends Control {
    private $input: JQuery;
    private decimalPlaces: number;
    private value: BehaviorSubject<string>;
    private additionalValidator: (raw: string, dec: decimal.Decimal) => boolean;

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);
        this.$element.addClass('control-container numericbox');
        this.decimalPlaces = this.config['content.DecimalPlaces'] || 0;

        this.$input = $('<input />')
            .css('text-align', this.config['content.TextAlignment'].toLowerCase())
            .val(this.config['content.Value'])
            .appendTo(this.$element);

        this.applyTooltip(this.$input);

        this.config.useJsNumbers = false;

        const focusout = rxui.focusout(this.$input).map(() => null);
        const enterPressed = Observable.fromEvent(this.$input, 'keydown').filter(x => (<any>x).keyCode === 13);
        const updateTrigger = focusout.let(x => this.config['content.UpdateOnEnter'] ? x.merge(enterPressed) : x);

        this.value = new BehaviorSubject(this.config['content.Value']);

        this.subscriptions = disposableSubscription(
            rxui.restrictInput(this.$input, this.value, updateTrigger, x => this.isValid(x), x => this.displayFormatToValueFormat(x), x => this.valueFormatToDisplayFormat(x), x => this.triggerEvent('Lost Focus and Value Changed')),
            this.value.distinctUntilChanged().subscribe(x => this.$element.triggerHandler('content.Value', x === null ? [null] : x)),
            this.processBindings(this.config),
            this.managePrimaryBinding('content.Value')
            //rxui.applyIE8SizeAndCenteringHackIfNecessary(this.$element, this.$input)
        );

        this.initialize();
    }

    private displayFormatToValueFormat(s: string): string {
        if (s === null || s === '' || s === undefined)
            return null;

        var d = new Decimal(this.stripGroupSeparators(s).replace(this.resources.getFormat('NumberDecimalSeparator'), '.'));
        return d.toFixed(this.decimalPlaces);
    }

    private valueFormatToDisplayFormat(s: string): string {
        if (s === null || s === undefined)
            return '';

        var d = new Decimal(s);

        return d.toFixed(this.decimalPlaces).replace(/\./, this.resources.getFormat('NumberDecimalSeparator'));
    }

    private isValid(s: string) {
        try {
            const d = (s === null || s === '') ? null : new Decimal(this.displayFormatToValueFormat(s));

            if (this.additionalValidator && !this.additionalValidator(s, d))
                return false;

            if (s === null || s === '')
                return true;

            return !d.isFinite() ? false
                : d.lessThan(minValue) ? false
                : d.greaterThan(maxValue) ? false
                : true;
        }
        catch (e) {
            return false;
        }
    }

    private stripGroupSeparators(s: string): string {
        var sep = this.resources.getFormat('NumberGroupSeparator');
        return sep === '' ? s : s.replace(new RegExp(escapeRegex(sep)), '');
    }

    'content.Value'(value: string | number) {
        this.checkInitialValueReceived();
        this.value.next((value === null || value === undefined || value === '') ? null : new Decimal(value.toString()).toFixed(this.decimalPlaces));
    }

    'set Value'(value: string | number) {
        this['content.Value'](value);
    }

    setAdditionalValidator(validator: (raw: string, dec: decimal.Decimal) => boolean) {
        this.additionalValidator = validator;
    }

    focus() {
        this.$input.focus();
    }

    applyEnabledState(value: boolean) {
        this.$input.prop('readonly', !value);
    }

    tabIndexElements() {
        return this.$input;
    }
}

registerBinding('numericbox', NumericBox);